<template>
  <transition name="audit-drawer">
    <div v-if="recordId" class="audit-drawer-layer">
      <div class="audit-drawer-backdrop" @click="emit('close')"></div>
      <aside class="audit-drawer" role="dialog" aria-modal="true">
        <header class="audit-drawer-head">
          <div class="audit-drawer-head-text">
            <span class="audit-drawer-eyebrow">Audit History</span>
            <h3 class="audit-drawer-title">{{ record.voucher_no || ('#' + recordId) }}</h3>
            <span class="audit-drawer-meta">
              #{{ recordId }}
            </span>
          </div>
          <button type="button" class="audit-drawer-close" @click="emit('close')">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>
        <div class="audit-drawer-body">
          <div v-if="loading" class="audit-drawer-loading">
            <span v-for="n in 6" :key="'line-' + n" class="audit-drawer-skeleton"></span>
          </div>
          <div v-else-if="! hasDetail" class="audit-drawer-empty">{{ errorMessage || 'Record not found.' }}</div>
          <template v-else>
            <section class="audit-section">
              <h4 class="audit-section-title">Record</h4>
              <dl class="audit-fields">
                <div v-for="field in recordFields" :key="field.label" class="audit-field">
                  <dt>{{ field.label }}</dt>
                  <dd>{{ field.value }}</dd>
                </div>
              </dl>
            </section>
            <section class="audit-section">
              <h4 class="audit-section-title">Audit Metadata</h4>
              <div class="audit-meta-grid">
                <div class="audit-meta-card">
                  <span class="audit-meta-label">Created</span>
                  <span class="audit-meta-user" :class="{ 'is-missing': ! record.created_by_name }">{{ auditUserLabel(record.created_by_id, record.created_by_name) }}</span>
                  <span class="audit-meta-time">{{ record.formatted_created_at }}</span>
                </div>
                <div class="audit-meta-card">
                  <span class="audit-meta-label">Last Updated <small>(latest update only)</small></span>
                  <span class="audit-meta-user" :class="{ 'is-missing': ! record.updated_by_name }">{{ auditUserLabel(record.updated_by_id, record.updated_by_name) }}</span>
                  <span class="audit-meta-time">
                    {{ record.formatted_updated_at }}
                    <span v-if="! record.is_modified" class="audit-meta-tag">Not modified since creation</span>
                  </span>
                </div>
              </div>
            </section>
            <section class="audit-section">
              <h4 class="audit-section-title">Voucher Lines</h4>
              <div v-if="! lines.length" class="audit-muted">No lines recorded.</div>
              <div v-else class="table-responsive">
                <table class="table audit-lines">
                  <thead>
                    <tr>
                      <th>Account Head</th>
                      <th>Party / Dimension</th>
                      <th class="text-end">Debit</th>
                      <th class="text-end">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="line in lines" :key="line.id">
                      <td>
                        <div class="audit-line-head">{{ line.account_head_name || '—' }}</div>
                        <div v-if="line.account_code" class="audit-line-sub">{{ line.account_code }}</div>
                      </td>
                      <td>
                        <div>{{ lineDimension(line) }}</div>
                        <div v-if="line.note" class="audit-line-sub">{{ line.note }}</div>
                      </td>
                      <td class="text-end">{{ amountText(line.debit) }}</td>
                      <td class="text-end">{{ amountText(line.credit) }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <th colspan="2">Total</th>
                      <th class="text-end">{{ amountText(lineTotal('debit')) }}</th>
                      <th class="text-end">{{ amountText(lineTotal('credit')) }}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
            <section v-if="attachments.length" class="audit-section">
              <h4 class="audit-section-title">Attachments</h4>
              <ul class="audit-attachments">
                <li v-for="file in attachments" :key="file.id">
                  <a :href="file.file_path" target="_blank" rel="noopener">
                    <i class="fa-solid fa-paperclip"></i>
                    {{ file.name }}
                  </a>
                </li>
              </ul>
            </section>
            <section class="audit-section">
              <h4 class="audit-section-title">History</h4>
              <div v-if="! detail.has_field_history" class="audit-notice">
                <span class="audit-notice-icon">
                  <i class="fa-solid fa-circle-info"></i>
                </span>
                <span>Field-level changes (previous and new values) are not recorded for these records. Only creation, the latest update and approval actions are available.</span>
              </div>
              <div v-if="! events.length" class="audit-muted">No history events recorded.</div>
              <ol v-else class="audit-timeline">
                <li v-for="event in events" :key="event.type + '-' + event.id" class="audit-timeline-item">
                  <span class="audit-timeline-dot" :class="auditStatusTone(event.status)"></span>
                  <div class="audit-timeline-content">
                    <div class="audit-timeline-top">
                      <span class="audit-status" :class="auditStatusTone(event.status)">{{ auditTypeLabel(event.status) }}</span>
                      <span class="audit-timeline-kind">{{ eventKind(event) }}</span>
                    </div>
                    <div class="audit-timeline-actor">
                      by <strong :class="{ 'is-missing': ! event.actor_name }">{{ auditUserLabel(event.actor_id, event.actor_name) }}</strong>
                    </div>
                    <div v-if="event.note" class="audit-timeline-note">“{{ event.note }}”</div>
                    <div class="audit-timeline-time">{{ event.formatted_at }}</div>
                  </div>
                </li>
              </ol>
            </section>
          </template>
        </div>
        <footer class="audit-drawer-foot">
          <button type="button" class="btn audit-drawer-btn" @click="emit('close')">Back to results</button>
          <button
              type="button"
              class="btn btn-primary audit-drawer-btn"
              :disabled="! canPrint"
              @click="emit('print', record)"
          >
            <i class="fa-solid fa-print"></i>
            Print Voucher
          </button>
        </footer>
      </aside>
    </div>
  </transition>
</template>

<script setup>
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import handleAuditTrail from '@/services/modules/auditTrail'
import { commaFormat } from '@/services/utils/global'
import { auditStatusTone, auditTypeLabel, auditUserLabel } from '@/services/utils/auditTrailFormat'

const props = defineProps({
  recordId: {
    type: [String, Number],
    default: ''
  },
  printableTypes: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'print'])

const route = useRoute()
const showError = inject('showError')
const { fetchAuditTrailDetail } = handleAuditTrail()

const loading = ref(false)
const detail = ref({})
const errorMessage = ref('')

const hasDetail = computed(() => !! detail.value.record)
const record = computed(() => detail.value.record || {})
const lines = computed(() => detail.value.lines || [])
const attachments = computed(() => detail.value.attachments || [])
const events = computed(() => detail.value.events || [])

const canPrint = computed(() => {
  if (! record.value.voucher_type) return false
  return props.printableTypes.includes(record.value.voucher_type)
})

const emptyValue = '—'

const textOrDash = (value) => {
  if (value === null || value === undefined || value === '') return emptyValue
  return value
}

const amountText = (value) => {
  if (! Number(value)) return '—'
  return commaFormat(value)
}

const recordFields = computed(() => {
  const item = record.value
  return [
    { label: 'Voucher Type', value: auditTypeLabel(item.voucher_type) },
    { label: 'Transaction Type', value: auditTypeLabel(item.txn_type) },
    { label: 'Status', value: auditTypeLabel(item.status) },
    { label: 'Voucher No', value: textOrDash(item.voucher_no) },
    { label: 'Txn No', value: textOrDash(item.txn_no) },
    { label: 'Voucher Date', value: textOrDash(item.formatted_date) },
    { label: 'Total Amount', value: amountText(item.total_amount) },
    { label: 'VAT Amount', value: amountText(item.vat_amount) },
    { label: 'Party', value: textOrDash(item.party_name) },
    { label: 'Business Unit', value: textOrDash(item.business_name) },
    { label: 'MOP Reference', value: textOrDash(item.mop_references) },
    { label: 'Payment Ref', value: textOrDash(item.payment_ref) },
    { label: 'Payment Ref Date', value: textOrDash(item.formatted_payment_ref_date) },
    { label: 'Upload Batch', value: item.ledger_upload_log_id ? '#' + item.ledger_upload_log_id : '—' },
    { label: 'Note', value: textOrDash(item.note) }
  ].filter(field => field.value !== emptyValue)
})

const lineDimension = (line) => {
  const parts = [line.party_name, line.business_name, line.cost_centre_name, line.project_name].filter(part => !! part)
  if (! parts.length) return '—'
  return parts.join(' · ')
}

const lineTotal = (key) => {
  return lines.value.reduce((total, line) => total + Number(line[key] || 0), 0)
}

const eventKind = (event) => {
  if (event.type !== 'workflow') return 'Voucher approval'
  return `Workflow step ${event.step_number}`
}

const getDetail = async () => {
  detail.value = {}
  errorMessage.value = ''
  if (! props.recordId) return
  loading.value = true
  try {
    const res = await fetchAuditTrailDetail(props.recordId, `?company_id=${route.params.companyId}`)
    if (! res.status) {
      errorMessage.value = res.message
      return
    }
    detail.value = res.data
  } catch (err) {
    showError('Something went wrong')
  } finally {
    loading.value = false
  }
}

const onKeydown = (event) => {
  if (! props.recordId) return
  if (event.key !== 'Escape') return
  emit('close')
}

watch(() => props.recordId, getDetail, { immediate: true })

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped>
.audit-drawer-layer {
  position: fixed;
  inset: 0;
  z-index: 1060;
}
.audit-drawer-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, .45);
}
.audit-drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: 620px;
  max-width: 100%;
  background: #fff;
  box-shadow: -12px 0 32px rgba(16, 24, 40, .16);
}
.audit-drawer-enter-active,
.audit-drawer-leave-active {
  transition: opacity .2s ease;
}
.audit-drawer-enter-active .audit-drawer,
.audit-drawer-leave-active .audit-drawer {
  transition: transform .2s ease;
}
.audit-drawer-enter-from,
.audit-drawer-leave-to {
  opacity: 0;
}
.audit-drawer-enter-from .audit-drawer,
.audit-drawer-leave-to .audit-drawer {
  transform: translateX(40px);
}
.audit-drawer-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #eaecf0;
}
.audit-drawer-head-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.audit-drawer-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: #7367f0;
}
.audit-drawer-title {
  margin: 2px 0;
  font-size: 18px;
  font-weight: 600;
  color: #101828;
  word-break: break-all;
}
.audit-drawer-meta {
  font-size: 12px;
  color: #667085;
}
.audit-drawer-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: #f2f4f7;
  color: #475467;
}
.audit-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}
.audit-drawer-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #eaecf0;
}
.audit-drawer-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  font-size: 13px;
}
.audit-drawer-btn:not(.btn-primary) {
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #344054;
}
.audit-drawer-loading {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.audit-drawer-skeleton {
  display: block;
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e4e7ec 50%, #f2f4f7 75%);
  background-size: 200% 100%;
  animation: audit-shimmer 1.2s infinite;
}
.audit-drawer-empty {
  padding: 32px 0;
  text-align: center;
  color: #667085;
}
.audit-section {
  margin-bottom: 20px;
}
.audit-section-title {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .05em;
  text-transform: uppercase;
  color: #475467;
}
.audit-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
  margin: 0;
}
.audit-field dt {
  font-size: 11px;
  font-weight: 500;
  color: #667085;
}
.audit-field dd {
  margin: 2px 0 0;
  font-size: 13px;
  color: #101828;
  word-break: break-word;
}
.audit-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.audit-meta-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #f9fafb;
}
.audit-meta-label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
}
.audit-meta-label small {
  font-weight: 400;
}
.audit-meta-user {
  font-size: 14px;
  font-weight: 600;
  color: #101828;
}
.audit-meta-time {
  font-size: 12px;
  color: #475467;
}
.audit-meta-tag {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #039855;
}
.is-missing {
  font-style: italic;
  color: #b42318;
}
.audit-lines {
  margin: 0;
  font-size: 12px;
}
.audit-lines th,
.audit-lines td {
  padding: 8px 10px !important;
  border-bottom: 1px solid #f2f4f7;
  vertical-align: top;
}
.audit-lines thead th {
  background: #f9fafb;
  font-size: 11px;
  font-weight: 600;
  color: #475467;
}
.audit-lines tfoot th {
  border-bottom: 0;
  background: #f9fafb;
}
.audit-line-head {
  font-weight: 500;
  color: #101828;
}
.audit-line-sub {
  font-size: 11px;
  color: #667085;
}
.audit-attachments {
  margin: 0;
  padding: 0;
  list-style: none;
}
.audit-attachments li {
  padding: 6px 0;
  font-size: 13px;
}
.audit-attachments a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #7367f0;
}
.audit-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #b2ddff;
  border-radius: 8px;
  background: #eff8ff;
  font-size: 12px;
  color: #175cd3;
}
.audit-notice-icon {
  margin-top: 1px;
}
.audit-muted {
  font-size: 13px;
  color: #667085;
}
.audit-timeline {
  position: relative;
  margin: 0;
  padding: 0 0 0 18px;
  list-style: none;
}
.audit-timeline::before {
  content: '';
  position: absolute;
  top: 6px;
  bottom: 6px;
  left: 5px;
  width: 2px;
  background: #eaecf0;
}
.audit-timeline-item {
  position: relative;
  padding-bottom: 14px;
}
.audit-timeline-dot {
  position: absolute;
  top: 5px;
  left: -18px;
  width: 12px;
  height: 12px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #98a2b3;
  box-shadow: 0 0 0 1px #eaecf0;
}
.audit-timeline-dot.is-success {
  background: #12b76a;
}
.audit-timeline-dot.is-danger {
  background: #f04438;
}
.audit-timeline-dot.is-warning {
  background: #f79009;
}
.audit-timeline-content {
  font-size: 13px;
  color: #344054;
}
.audit-timeline-top {
  display: flex;
  align-items: center;
  gap: 8px;
}
.audit-timeline-kind {
  font-size: 12px;
  color: #667085;
}
.audit-timeline-actor {
  margin-top: 2px;
}
.audit-timeline-note {
  margin-top: 2px;
  font-style: italic;
  color: #475467;
}
.audit-timeline-time {
  margin-top: 2px;
  font-size: 12px;
  color: #98a2b3;
}
.audit-status {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  background: #f2f4f7;
  font-size: 11px;
  font-weight: 600;
  color: #475467;
}
.audit-status.is-success {
  background: #ecfdf3;
  color: #027a48;
}
.audit-status.is-danger {
  background: #fef3f2;
  color: #b42318;
}
.audit-status.is-warning {
  background: #fffaeb;
  color: #b54708;
}
@keyframes audit-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
@media (max-width: 576px) {
  .audit-fields,
  .audit-meta-grid {
    grid-template-columns: 1fr;
  }
}
</style>
